/* ==========================================================================
   SkinTalk.mx - Product Form Custom Element
   AJAX POST to /cart/add.js. Dispatches cart:updated. Loading state. Error handling.
   ========================================================================== */

if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form[data-type="add-to-cart-form"]');
      this.submitButton = this.querySelector('[data-add-to-cart]');
      this.submitText = this.querySelector('[data-add-to-cart-text]');
      this.spinner = this.querySelector('.product-form__spinner');
      this.errorWrapper = this.querySelector('.product-form__error-wrapper');
      this.errorMessage = this.querySelector('[data-error-message]');
      this.sectionId = this.dataset.sectionId;
    }

    connectedCallback() {
      if (!this.form) return;

      this.form.addEventListener('submit', this.onSubmit.bind(this));

      /* Listen for variant changes to update the hidden variant ID input */
      document.addEventListener('variant:changed', (e) => {
        if (e.detail?.sectionId === this.sectionId) {
          this.onVariantChange(e.detail.variant);
        }
      });

      /* Listen for unavailable variant selections */
      document.addEventListener('variant:unavailable', (e) => {
        if (e.detail?.sectionId === this.sectionId) {
          this.onVariantUnavailable();
        }
      });
    }

    /**
     * Handle variant changes - update hidden input and button state.
     * @param {Object} variant - The selected variant object.
     */
    onVariantChange(variant) {
      const variantInput = this.form.querySelector('[data-variant-id]');
      if (variantInput) {
        variantInput.value = variant.id;
      }

      if (variant.available) {
        this.submitButton.disabled = false;
        if (this.submitText) {
          this.submitText.textContent = 'Agregar al Carrito';
        }
      } else {
        this.submitButton.disabled = true;
        if (this.submitText) {
          this.submitText.textContent = 'Agotado';
        }
      }

      this.hideError();
    }

    /**
     * Handle unavailable variant combination.
     */
    onVariantUnavailable() {
      this.submitButton.disabled = true;
      if (this.submitText) {
        this.submitText.textContent = 'No Disponible';
      }
    }

    /**
     * Handle form submission via AJAX.
     * @param {Event} event - The submit event.
     */
    async onSubmit(event) {
      event.preventDefault();

      if (this.submitButton.disabled || this.submitButton.classList.contains('loading')) {
        return;
      }

      this.setLoadingState(true);
      this.hideError();

      const formData = new FormData(this.form);
      const body = {};

      for (const [key, value] of formData.entries()) {
        if (key === 'id') {
          body.id = parseInt(value, 10);
        } else if (key === 'quantity') {
          body.quantity = parseInt(value, 10);
        } else {
          body[key] = value;
        }
      }

      /* Add section rendering for cart drawer updates */
      body.sections = 'cart-drawer,cart-icon-bubble';
      body.sections_url = window.location.pathname;

      try {
        const config = SkinTalk.fetchConfig();
        config.body = JSON.stringify(body);

        const response = await fetch(SkinTalk.routes.cart_add_url + '.js', config);
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.description || data.message || 'Error al agregar al carrito');
        }

        /* Fetch updated cart for count */
        const cartResponse = await fetch(SkinTalk.routes.cart_js_url);
        const cart = await cartResponse.json();

        /* Publish cart updated event */
        SkinTalk.publish('cart:updated', cart);
        SkinTalk.publish('product:added', {
          product: data,
          cart: cart
        });
        /* Abrir cart drawer (no redirigir a /cart) */
        SkinTalk.publish('drawer:open', { type: 'cart' });

        /* Brief success feedback */
        if (this.submitText) {
          const originalText = this.submitText.textContent;
          this.submitText.textContent = 'Agregado!';
          setTimeout(() => {
            this.submitText.textContent = originalText;
          }, 2000);
        }

        /* Dispatch DOM event for other components */
        document.dispatchEvent(new CustomEvent('cart:item-added', {
          detail: { item: data, cart: cart }
        }));

      } catch (error) {
        console.error('[SkinTalk] Add to cart error:', error);
        this.showError(error.message || 'Hubo un error. Intenta de nuevo.');
      } finally {
        this.setLoadingState(false);
      }
    }

    /**
     * Toggle loading state on the submit button.
     * @param {boolean} isLoading - Whether loading state is active.
     */
    setLoadingState(isLoading) {
      if (isLoading) {
        this.submitButton.classList.add('loading');
        this.submitButton.setAttribute('aria-busy', 'true');
        if (this.spinner) this.spinner.hidden = false;
      } else {
        this.submitButton.classList.remove('loading');
        this.submitButton.removeAttribute('aria-busy');
        if (this.spinner) this.spinner.hidden = true;
      }
    }

    /**
     * Show an error message.
     * @param {string} message - The error message to display.
     */
    showError(message) {
      if (this.errorWrapper && this.errorMessage) {
        this.errorMessage.textContent = message;
        this.errorWrapper.hidden = false;
        this.errorWrapper.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }

    /**
     * Hide the error message.
     */
    hideError() {
      if (this.errorWrapper) {
        this.errorWrapper.hidden = true;
      }
    }
  }

  customElements.define('product-form', ProductForm);
}
